import { Result, Spin } from "antd";
import React, { Suspense } from "react";
import { Route, BrowserRouter as Router, Switch } from "react-router-dom";
import useAuth from "../hooks/useAuth";
import AppConfig from "../screens/appConfig";
import Forbidden from "../screens/auth/Forbidden";
import CreatePost from "../screens/blog/posts/Create";
import DetailPost from "../screens/blog/posts/Detail";
import { EEmployeePageType } from "../screens/employee/constant";
import ExchangeBoard from "../screens/exchangeBoard";
import ListingHasPortal from "../screens/listing/ListingHasPortal";
import ListingNotWatermark from "../screens/listing/ListingNotWatermark";
import MaskingList from "../screens/masking";
import MaskingListingDetail from "../screens/masking/Detail";
import SimpleDetailOffice from "../screens/officeDetail/SimpleDetailOffice";
import Preference from "../screens/preference";
import PortalChecker from "../screens/portalChecker";
import TransferListingAgent from "../screens/transferListing";
import CreateTransferListing from "../screens/transferListing/Create";
import TransferListingDetail from "../screens/transferListing/Detail";
import TransferMember from "../screens/transferMember";
import CreateTransferMember from "../screens/transferMember/Create";
import TransferMemberDetail from "../screens/transferMember/Detail";
import Public from "../screens/user/Public";
import { EEmployeeRole } from "../types/employee.type";
import PrivateRoute from "./PrivateRoute";
import PublicRoute from "./PublicRoute";

const Login = React.lazy(() => import("../screens/auth/Login"));
const ForgotPassword = React.lazy(() => import("../screens/auth/ForgotPassword"));
const ResetPassword = React.lazy(() => import("../screens/auth/ResetPassword"));
const SetToken = React.lazy(() => import("../screens/auth/SetToken"));
const VerifyEmail = React.lazy(() => import("../screens/auth/VerifyEmail"));

const MyProfile = React.lazy(() => import("../screens/profile"));
const EditMyProfile = React.lazy(() => import("../screens/profile/Edit"));
const ChangeMyPassword = React.lazy(() => import("../screens/profile/ChangeMyPassword"));
const Dashboard = React.lazy(() => import("../screens/dashboard"));
const ChangePasswordUser = React.lazy(() => import("../screens/user/ChangePassword"));
const User = React.lazy(() => import("../screens/user"));
const UserEdit = React.lazy(() => import("../screens/user/Edit"));
const UserDetail = React.lazy(() => import("../screens/user/Detail"));
const UserEmails = React.lazy(() => import("../screens/user-emails"));
const LoginActivity = React.lazy(() => import("../screens/loginActivity"));

const MasterDataAchievement = React.lazy(() => import("../screens/masterDataAchievement"));
const MasterDataCareer = React.lazy(() => import("../screens/masterDataCareer"));
const MasterDataFee = React.lazy(() => import("../screens/masterDataFee"));
const MasterDataPropertyType = React.lazy(() => import("../screens/masterDataPropertyType"));

const Employee = React.lazy(() => import("../screens/employee"));
const EmployeeAdd = React.lazy(() => import("../screens/employee/Add"));
const EmployeeEdit = React.lazy(() => import("../screens/employee/Edit"));
const EmployeeApproval = React.lazy(() => import("../screens/employeeApproval"));
const EmployeeDetail = React.lazy(() => import("../screens/employeeDetail/DetailApprove"));
const EmployeeDefaultDetail = React.lazy(() => import("../screens/employeeDetail/DetailDefault"));

const Office = React.lazy(() => import("../screens/office"));
const OfficeAdd = React.lazy(() => import("../screens/office/Add"));
const OfficeApproval = React.lazy(() => import("../screens/officeApproval"));
const OfficeDetail = React.lazy(() => import("../screens/officeDetail"));

const Event = React.lazy(() => import("../screens/event"));
const EventAdd = React.lazy(() => import("../screens/event/Add"));
const EventDetail = React.lazy(() => import("../screens/event/Detail"));
const EMagazine = React.lazy(() => import("../screens/eMagazine"));
const DocumentCenter = React.lazy(() => import("../screens/documentCenter"));

const Partner = React.lazy(() => import("../screens/partner"));

const GroupOfCity = React.lazy(() => import("../screens/groupOfCity"));

const ListingList = React.lazy(() => import("../screens/listing"));
const CreateListing = React.lazy(() => import("../screens/listing/Create"));
const ListingDetail = React.lazy(() => import("../screens/listing/Detail"));

const Group = React.lazy(() => import("../screens/group"));
const CreateGroup = React.lazy(() => import("../screens/group/Create"));
const GroupDetail = React.lazy(() => import("../screens/group/Detail"));

const PrimaryProject = React.lazy(() => import("../screens/primaryProject"));
const CreatePrimaryProject = React.lazy(() => import("../screens/primaryProject/Create"));
const DetailPrimaryProject = React.lazy(() => import("../screens/primaryProject/Detail"));

const TransferEmployee = React.lazy(() => import("../screens/transferEmployee"));
const CreateTransferEmployee = React.lazy(() => import("../screens/transferEmployee/Create"));
const TransferEmployeeDetail = React.lazy(() => import("../screens/transferEmployee/Detail"));

const Closing = React.lazy(() => import("../screens/closing"));
const CreateClosing = React.lazy(() => import("../screens/closing/Create"));
const DetailClosing = React.lazy(() => import("../screens/closing/Detail"));

const Invoice = React.lazy(() => import("../screens/invoice"));
const DetailInvoice = React.lazy(() => import("../screens/invoice/Detail"));

const AgentCommission = React.lazy(() => import("../screens/agentCommission"));

const OfficeBilling = React.lazy(() => import("../screens/officeBilling"));
const CreateOfficeBilling = React.lazy(() => import("../screens/officeBilling/Create"));
const DetailOfficeBilling = React.lazy(() => import("../screens/officeBilling/Detail"));

const Portal = React.lazy(() => import("../screens/portal"));
const DetailPortal = React.lazy(() => import("../screens/portal/Detail"));

const SettingPortal = React.lazy(() => import("../screens/portalSetting"));

const SettingAgent = React.lazy(() => import("../screens/settingAgent"));

const Handover = React.lazy(() => import("../screens/handover"));
const HandoverCreate = React.lazy(() => import("../screens/handover/Create"));
const HandoverDetail = React.lazy(() => import("../screens/handover/Detail"));

const Client = React.lazy(() => import("../screens/client"));
const ClientDetail = React.lazy(() => import("../screens/client/Detail"));

const AgentCalculation = React.lazy(() => import("../screens/agentCalculation"));
const DetailAgentCalculation = React.lazy(() => import("../screens/agentCalculation/Detail"));

const Banner = React.lazy(() => import("../screens/banner"));
const Album = React.lazy(() => import("../screens/album"));
const CreateAlbum = React.lazy(() => import("../screens/album/Create"));
const DetailAlbum = React.lazy(() => import("../screens/album/Detail"));
const GetLocalStorage = React.lazy(() => import("../screens/auth/GetLocalStorage"));

const TemplateMessage = React.lazy(() => import("../screens/templateMessage"));
const EditTemplateMessage = React.lazy(() => import("../screens/templateMessage/Edit"));

const Posts = React.lazy(() => import("../screens/blog/posts"));
const Categories = React.lazy(() => import("../screens/blog/categories"));
const Tags = React.lazy(() => import("../screens/blog/tags"));

const MyLists = React.lazy(() => import("../screens/myList"));
const CreateMyList = React.lazy(() => import("../screens/myList/Create"));
const DetailMyList = React.lazy(() => import("../screens/myList/Detail"));

const ConfigAppLogo = React.lazy(() => import("../screens/configAppLogo"));

const RootNavigator = () => {
	const {
		isHavePermission,
		isAdminHQPlus,
		isHQ,
		isHQManagerPlus,
		isAdminOfficePlus,
		isFOPlus,
		isBOPlus,
		isAdminHQWithFinancePlus,
		isHQFinance,
		isAdminHQWithOfficeFinancePlusAndAgent,
		isAdminHQOfficeNonFinancePlus,
		isAgentOrAO,
		isOfficeManagementWithFinance,
		isAgent,
		isAdminHQOfficeFinancePlus,
		isAdminHQAndAdminOffices,
		isAdminHQWithOfficeFinance,
		isAdminBlog,
		isFranchiseOrBusinessAdmin,
		isAgentListing,
		roles,
		isHQAndOfficeManagementNonFinance,
	} = useAuth();

	return (
		<Suspense
			fallback={
				<Spin size="large">
					<div style={{ width: "100vw", height: "100vh" }} />
				</Spin>
			}
		>
			<Router>
				<Switch>
					<Route path="/set-token">
						<SetToken />
					</Route>
					<Route path="/get-local-storage">
						<GetLocalStorage />
					</Route>

					{/* Public Route */}

					<PublicRoute
						path="/reset-password/:sessionId"
						restricted={true}
					>
						<ResetPassword />
					</PublicRoute>
					<PublicRoute
						exact
						path="/"
						restricted={true}
					>
						<Login />
					</PublicRoute>
					<PublicRoute
						exact
						path="/login"
						restricted={true}
					>
						<Login />
					</PublicRoute>
					<PublicRoute
						exact
						path="/forgot-password"
						restricted={true}
					>
						<ForgotPassword />
					</PublicRoute>
					<PublicRoute
						path="/verify-email/:sessionId"
						restricted={true}
					>
						<VerifyEmail />
					</PublicRoute>

					{/* Private Route */}
					<PrivateRoute
						exact
						path="/dashboard"
					>
						<Dashboard />
					</PrivateRoute>

					<PrivateRoute
						exact
						path="/profile/edit"
					>
						<EditMyProfile />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/profile/edit/:index"
					>
						<EditMyProfile />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/profile"
					>
						<MyProfile />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/profile/:index"
					>
						<MyProfile />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/change-password"
					>
						<ChangeMyPassword />
					</PrivateRoute>

					<PrivateRoute
						exact
						path="/users"
						permission={isHavePermission(EEmployeeRole.admin)}
					>
						<User />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/user-has-remax-emails"
						// permission={isHavePermission(EEmployeeRole.admin)}
					>
						<UserEmails />
					</PrivateRoute>

					<PrivateRoute
						exact
						path="/public-users"
						permission={isHavePermission(EEmployeeRole.admin)}
					>
						<Public />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/public-users/:userId"
						permission={isHavePermission(EEmployeeRole.admin)}
					>
						<UserDetail />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/users/add"
						permission={isHavePermission(EEmployeeRole.admin)}
					>
						<UserEdit />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/users/:userId/edit"
						permission={isHavePermission(EEmployeeRole.admin)}
					>
						<UserEdit />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/users/:userId/change-password"
						permission={isHavePermission(EEmployeeRole.admin)}
					>
						<ChangePasswordUser />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/users/:userId"
						permission={isHavePermission(EEmployeeRole.admin)}
					>
						<UserDetail />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/users/:userId/:tab"
						permission={isHavePermission(EEmployeeRole.admin)}
					>
						<UserDetail />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/login-activity"
						permission={isHQ}
					>
						<LoginActivity />
					</PrivateRoute>

					<PrivateRoute
						exact
						path="/master-data-achievement"
						permission={isAdminHQPlus}
					>
						<MasterDataAchievement />
					</PrivateRoute>

					<PrivateRoute
						exact
						path="/master-data-career"
						permission={isAdminHQPlus}
					>
						<MasterDataCareer />
					</PrivateRoute>

					<PrivateRoute
						exact
						path="/master-data-fee"
						permission={isAdminHQPlus}
					>
						<MasterDataFee />
					</PrivateRoute>

					<PrivateRoute
						exact
						path="/master-data-property-types"
						permission={isAdminHQPlus}
					>
						<MasterDataPropertyType />
					</PrivateRoute>

					{/* HQ */}
					<PrivateRoute
						exact
						path="/hq"
						permission={isHQManagerPlus}
					>
						<Employee type={EEmployeePageType.HQ} />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/hq/create"
						permission={isHQManagerPlus}
					>
						<EmployeeAdd type={EEmployeePageType.HQ} />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/hq/create/:employeeId"
						permission={isHQManagerPlus}
					>
						<EmployeeAdd type={EEmployeePageType.HQ} />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/hq/create/:employeeId/:index"
						permission={isHQManagerPlus}
					>
						<EmployeeAdd type={EEmployeePageType.HQ} />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/hq/:userId/edit"
						permission={isHQManagerPlus}
					>
						<EmployeeEdit type={EEmployeePageType.HQ} />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/hq/:userId/edit/:index"
						permission={isHQManagerPlus}
					>
						<EmployeeEdit type={EEmployeePageType.HQ} />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/hq/:userId"
						permission={isHQManagerPlus}
					>
						<EmployeeDefaultDetail type={EEmployeePageType.HQ} />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/hq/:userId/:tab"
						permission={isHQManagerPlus}
					>
						<EmployeeDefaultDetail type={EEmployeePageType.HQ} />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/hq/:userId/:tab/:index"
						permission={isHQManagerPlus}
					>
						<EmployeeDefaultDetail type={EEmployeePageType.HQ} />
					</PrivateRoute>

					{/* AUTHOR */}
					<PrivateRoute
						exact
						path="/author"
						permission={isAdminBlog}
					>
						<Employee type={EEmployeePageType.AUTHOR} />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/author/create"
						permission={isAdminBlog}
					>
						<EmployeeAdd type={EEmployeePageType.AUTHOR} />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/author/create/:employeeId"
						permission={isAdminBlog}
					>
						<EmployeeAdd type={EEmployeePageType.AUTHOR} />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/author/create/:employeeId/:index"
						permission={isAdminBlog}
					>
						<EmployeeAdd type={EEmployeePageType.AUTHOR} />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/author/:userId/edit"
						permission={isAdminBlog}
					>
						<EmployeeEdit type={EEmployeePageType.AUTHOR} />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/author/:userId/edit/:index"
						permission={isAdminBlog}
					>
						<EmployeeEdit type={EEmployeePageType.AUTHOR} />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/author/:userId"
						permission={isAdminBlog}
					>
						<EmployeeDefaultDetail type={EEmployeePageType.AUTHOR} />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/author/:userId/:tab"
						permission={isAdminBlog}
					>
						<EmployeeDefaultDetail type={EEmployeePageType.AUTHOR} />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/author/:userId/:tab/:index"
						permission={isAdminBlog}
					>
						<EmployeeDefaultDetail type={EEmployeePageType.AUTHOR} />
					</PrivateRoute>

					{/* FRANCHISE OWNER */}
					<PrivateRoute
						exact
						path="/franchise-owner"
						permission={isAdminHQPlus}
					>
						<Employee type={EEmployeePageType.FRANCHISE_OWNER} />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/franchise-owner/create"
						permission={isAdminHQPlus}
					>
						<EmployeeAdd type={EEmployeePageType.FRANCHISE_OWNER} />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/franchise-owner/create/:employeeId"
						permission={isAdminHQPlus}
					>
						<EmployeeAdd type={EEmployeePageType.FRANCHISE_OWNER} />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/franchise-owner/create/:employeeId/:index"
						permission={isAdminHQPlus}
					>
						<EmployeeAdd type={EEmployeePageType.FRANCHISE_OWNER} />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/franchise-owner/:userId/edit"
						permission={isAdminHQPlus}
					>
						<EmployeeEdit type={EEmployeePageType.FRANCHISE_OWNER} />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/franchise-owner/:userId/edit/:index"
						permission={isAdminHQPlus}
					>
						<EmployeeEdit type={EEmployeePageType.FRANCHISE_OWNER} />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/franchise-owner/:userId"
						permission={isAdminHQPlus}
					>
						<EmployeeDefaultDetail type={EEmployeePageType.FRANCHISE_OWNER} />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/franchise-owner/:userId/:tab"
						permission={isAdminHQPlus}
					>
						<EmployeeDefaultDetail type={EEmployeePageType.FRANCHISE_OWNER} />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/franchise-owner/:userId/:tab/:index"
						permission={isAdminHQPlus}
					>
						<EmployeeDefaultDetail type={EEmployeePageType.FRANCHISE_OWNER} />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/approve-franchise-owner"
						permission={isHQManagerPlus}
					>
						<EmployeeApproval type={EEmployeePageType.FRANCHISE_OWNER} />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/approve-franchise-owner/:employeeId"
						permission={isHQManagerPlus}
					>
						<EmployeeDetail type={EEmployeePageType.FRANCHISE_OWNER} />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/approve-franchise-owner/:employeeId/:index"
						permission={isHQManagerPlus}
					>
						<EmployeeDetail type={EEmployeePageType.FRANCHISE_OWNER} />
					</PrivateRoute>

					{/* AGENT */}
					<PrivateRoute
						exact
						path="/agent"
						permission={isAdminHQAndAdminOffices}
					>
						<Employee type={EEmployeePageType.AGENT} />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/agent/create"
						permission={isAdminHQAndAdminOffices}
					>
						<EmployeeAdd type={EEmployeePageType.AGENT} />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/agent/create/:employeeId"
						permission={isAdminHQAndAdminOffices}
					>
						<EmployeeAdd type={EEmployeePageType.AGENT} />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/agent/create/:employeeId/:index"
						permission={isAdminHQAndAdminOffices}
					>
						<EmployeeAdd type={EEmployeePageType.AGENT} />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/agent/:userId/edit"
						permission={isAdminHQAndAdminOffices}
					>
						<EmployeeEdit type={EEmployeePageType.AGENT} />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/agent/:userId/edit/:index"
						permission={isAdminHQAndAdminOffices}
					>
						<EmployeeEdit type={EEmployeePageType.AGENT} />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/agent/:userId"
						permission={isAdminHQAndAdminOffices}
					>
						<EmployeeDefaultDetail type={EEmployeePageType.AGENT} />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/agent/:userId/:tab"
						permission={isAdminHQAndAdminOffices}
					>
						<EmployeeDefaultDetail type={EEmployeePageType.AGENT} />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/agent/:userId/:tab/:index"
						permission={isAdminHQAndAdminOffices}
					>
						<EmployeeDefaultDetail type={EEmployeePageType.AGENT} />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/approve-agent"
						permission={isAdminHQPlus}
					>
						<EmployeeApproval type={EEmployeePageType.AGENT} />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/approve-agent/:employeeId"
						permission={isAdminHQPlus}
					>
						<EmployeeDetail type={EEmployeePageType.AGENT} />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/approve-agent/:employeeId/:index"
						permission={isAdminHQPlus}
					>
						<EmployeeDetail type={EEmployeePageType.AGENT} />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/transfer-listing/:transferListingId/detail"
						permission={isHQAndOfficeManagementNonFinance}
					>
						<TransferListingDetail />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/transfer-listing"
						permission={isHQAndOfficeManagementNonFinance}
					>
						<TransferListingAgent />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/transfer-listing/:type"
						permission={isHQAndOfficeManagementNonFinance}
					>
						<TransferListingAgent />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/transfer-listing/create/:userId"
						permission={isHQAndOfficeManagementNonFinance}
					>
						<CreateTransferListing />
					</PrivateRoute>

					{/* OFFICE PERSONNEL */}
					<PrivateRoute
						exact
						path="/staff"
						permission={isBOPlus}
					>
						<Employee type={EEmployeePageType.OFFICE_PERSONNEL} />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/staff/create"
						permission={isBOPlus}
					>
						<EmployeeAdd type={EEmployeePageType.OFFICE_PERSONNEL} />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/staff/create/:employeeId"
						permission={isBOPlus}
					>
						<EmployeeAdd type={EEmployeePageType.OFFICE_PERSONNEL} />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/staff/create/:employeeId/:index"
						permission={isBOPlus}
					>
						<EmployeeAdd type={EEmployeePageType.OFFICE_PERSONNEL} />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/staff/:userId/edit"
						permission={isBOPlus}
					>
						<EmployeeEdit type={EEmployeePageType.OFFICE_PERSONNEL} />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/staff/:userId/edit/:index"
						permission={isBOPlus}
					>
						<EmployeeEdit type={EEmployeePageType.OFFICE_PERSONNEL} />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/staff/:userId"
						permission={isBOPlus}
					>
						<EmployeeDefaultDetail type={EEmployeePageType.OFFICE_PERSONNEL} />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/staff/:userId/:tab"
						permission={isBOPlus}
					>
						<EmployeeDefaultDetail type={EEmployeePageType.OFFICE_PERSONNEL} />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/staff/:userId/:tab/:index"
						permission={isBOPlus}
					>
						<EmployeeDefaultDetail type={EEmployeePageType.OFFICE_PERSONNEL} />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/approve-staff"
						permission={isHQManagerPlus}
					>
						<EmployeeApproval type={EEmployeePageType.OFFICE_PERSONNEL} />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/approve-staff/:employeeId"
						permission={isHQManagerPlus}
					>
						<EmployeeDetail type={EEmployeePageType.OFFICE_PERSONNEL} />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/approve-staff/:employeeId/:index"
						permission={isHQManagerPlus}
					>
						<EmployeeDetail type={EEmployeePageType.OFFICE_PERSONNEL} />
					</PrivateRoute>

					{/* TRANSFER EMPLOYEE */}
					<PrivateRoute
						exact
						path="/transfer-staff"
						permission={isAdminHQPlus}
					>
						<TransferEmployee />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/transfer-staff/create"
						permission={isAdminHQPlus}
					>
						<CreateTransferEmployee />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/transfer-staff/:transferEmployeeId"
						permission={isAdminHQPlus}
					>
						<TransferEmployeeDetail />
					</PrivateRoute>

					{/* OFFICE */}
					<PrivateRoute
						exact
						path="/office"
						permission={isFOPlus}
					>
						<Office />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/detail-office"
						permission={isHavePermission(EEmployeeRole.admin_office)}
					>
						<SimpleDetailOffice />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/office/create"
						permission={isAdminHQPlus}
					>
						<OfficeAdd />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/office/create/:officeId"
						permission={isAdminHQPlus}
					>
						<OfficeAdd />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/office/create/:officeId/:index"
						permission={isAdminHQPlus}
					>
						<OfficeAdd />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/office/:officeId/edit"
						permission={isFOPlus}
					>
						<OfficeAdd isEdit={true} />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/office/:officeId/edit/:index"
						permission={isFOPlus}
					>
						<OfficeAdd isEdit={true} />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/office/:officeId"
						permission={isFOPlus}
					>
						<OfficeDetail type="default" />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/office/:officeId/:tab"
						permission={isFOPlus}
					>
						<OfficeDetail type="default" />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/office/:officeId/:tab/:index"
						permission={isFOPlus}
					>
						<OfficeDetail type="default" />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/approve-office"
						permission={isHQManagerPlus}
					>
						<OfficeApproval />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/approve-office/:officeId"
						permission={isHQManagerPlus}
					>
						<OfficeDetail type="approval" />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/approve-office/:officeId/:index"
						permission={isHQManagerPlus}
					>
						<OfficeDetail type="approval" />
					</PrivateRoute>

					<PrivateRoute
						exact
						path="/event"
						// permission={!isAdminHQOfficeFinancePlus}
					>
						<Event />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/event/create"
						permission={isAdminHQPlus}
					>
						<EventAdd />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/event/:eventId/edit"
						permission={isAdminHQPlus}
					>
						<EventAdd isEdit={true} />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/event/:eventId"
						// permission={!isAdminHQOfficeFinancePlus}
					>
						<EventDetail />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/event/:eventId/:tab"
						// permission={!isAdminHQOfficeFinancePlus}
					>
						<EventDetail />
					</PrivateRoute>

					<PrivateRoute
						exact
						path="/document-center"
						permission={isAdminHQPlus}
					>
						<DocumentCenter />
					</PrivateRoute>

					<PrivateRoute
						exact
						path="/request-board"
					>
						<ExchangeBoard />
					</PrivateRoute>

					<PrivateRoute
						exact
						path="/magazine"
						permission={isAdminHQPlus}
					>
						<EMagazine />
					</PrivateRoute>

					<PrivateRoute
						exact
						path="/partner"
						permission={isAdminHQPlus}
					>
						<Partner />
					</PrivateRoute>

					<PrivateRoute
						exact
						path="/group-of-city"
						permission={isAdminHQPlus}
					>
						<GroupOfCity />
					</PrivateRoute>

					<PrivateRoute
						exact
						path="/listing"
					>
						<ListingList />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/listing/create"
						permission={isAgentOrAO && isAgentListing}
					>
						<CreateListing />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/listing/create/:listingId"
						permission={isAdminHQOfficeNonFinancePlus || isAgentOrAO}
					>
						<CreateListing />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/listing/create/:listingId/:index"
						permission={isAdminHQOfficeNonFinancePlus || isAgentOrAO}
					>
						<CreateListing />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/listing/:listingId/edit"
						permission={isAdminHQOfficeNonFinancePlus || isAgentOrAO}
					>
						<CreateListing isEdit={true} />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/listing/:listingId/edit/:index"
						permission={isAdminHQOfficeNonFinancePlus || isAgentOrAO}
					>
						<CreateListing isEdit={true} />
					</PrivateRoute>
					<PrivateRoute
						permission={isAdminHQPlus}
						exact
						path="/listing-not-watermark"
					>
						<ListingNotWatermark />
					</PrivateRoute>
					<PrivateRoute
						permission={isAdminHQPlus}
						exact
						path="/listing-has-portal"
					>
						<ListingHasPortal />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/listing/:listingId/"
					>
						<ListingDetail />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/listing/:listingId/:tab"
					>
						<ListingDetail />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/listing/:listingId/:index"
					>
						<ListingDetail />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/listing/:listingId/:tab/:index"
					>
						<ListingDetail />
					</PrivateRoute>

					<PrivateRoute
						exact
						path="/masking"
					>
						<MaskingList />
					</PrivateRoute>

					<PrivateRoute
						exact
						path="/masking/:listingId"
					>
						<MaskingListingDetail />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/masking/:listingId/:index"
					>
						<MaskingListingDetail />
					</PrivateRoute>

					<PrivateRoute
						exact
						path="/transfer-team-member"
						permission={isAdminHQAndAdminOffices}
					>
						<TransferMember />
					</PrivateRoute>

					<PrivateRoute
						exact
						path="/transfer-team-member/create"
						permission={isAdminHQAndAdminOffices}
					>
						<CreateTransferMember />
					</PrivateRoute>

					<PrivateRoute
						exact
						path="/transfer-team-member/:transferMemberId"
						permission={isAdminHQAndAdminOffices}
					>
						<TransferMemberDetail />
					</PrivateRoute>

					<PrivateRoute
						exact
						path="/teams"
					>
						<Group />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/teams/create"
						permission={isAdminHQAndAdminOffices}
					>
						<CreateGroup />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/teams/create/:groupId/:index"
						permission={isAdminHQAndAdminOffices}
					>
						<CreateGroup />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/teams/:groupId/edit"
						permission={isAdminHQAndAdminOffices}
					>
						<CreateGroup isEdit={true} />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/teams/:groupId/edit/:index"
						permission={isAdminHQAndAdminOffices}
					>
						<CreateGroup isEdit={true} />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/teams/:groupId"
					>
						<GroupDetail />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/teams/:groupId/:index"
					>
						<GroupDetail />
					</PrivateRoute>

					<PrivateRoute
						exact
						path="/primary-project-coordination"
						permission={!isAdminHQOfficeFinancePlus}
					>
						<PrimaryProject />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/primary-project-coordination/create"
						permission={isAgentOrAO}
					>
						<CreatePrimaryProject />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/primary-project-coordination/:primaryProjectId/edit"
						permission={isAgentOrAO}
					>
						<CreatePrimaryProject isEdit={true} />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/primary-project-coordination/:primaryProjectId"
						permission={!isAdminHQOfficeFinancePlus}
					>
						<DetailPrimaryProject />
					</PrivateRoute>

					<PrivateRoute
						permission={isAdminHQWithOfficeFinancePlusAndAgent}
						exact
						path="/closing"
					>
						<Closing />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/closing/create"
						permission={isAdminHQWithOfficeFinance}
					>
						<CreateClosing />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/closing/create/:closingId"
						permission={isAdminHQWithOfficeFinance}
					>
						<CreateClosing />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/closing/create/:closingId/:index"
						permission={isAdminHQWithOfficeFinance}
					>
						<CreateClosing />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/closing/:closingId"
						permission={isAdminHQWithOfficeFinancePlusAndAgent}
					>
						<DetailClosing />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/closing/:closingId/history"
						permission={isAdminHQWithOfficeFinancePlusAndAgent}
					>
						<DetailClosing />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/closing/:closingId/history/:closingHistoryId"
						permission={isAdminHQWithOfficeFinancePlusAndAgent}
					>
						<DetailClosing />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/closing/:closingId/history/:closingHistoryId/:index"
						permission={isAdminHQWithOfficeFinancePlusAndAgent}
					>
						<DetailClosing />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/closing/:closingId/summary"
						permission={isAdminHQWithOfficeFinancePlusAndAgent}
					>
						<DetailClosing />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/closing/:closingId/:index"
						permission={isAdminHQWithOfficeFinancePlusAndAgent}
					>
						<DetailClosing />
					</PrivateRoute>

					<PrivateRoute
						exact
						path="/invoice"
						permission={isAdminHQWithOfficeFinancePlusAndAgent}
					>
						<Invoice />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/invoice/:invoiceId"
						permission={isAdminHQWithOfficeFinancePlusAndAgent}
					>
						<DetailInvoice />
					</PrivateRoute>

					{/* <PrivateRoute
						exact
						path="/commission"
					>
						<AgentCommission />
					</PrivateRoute> */}

					<PrivateRoute
						exact
						path="/billing"
						permission={isAdminHQWithFinancePlus}
					>
						<OfficeBilling />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/billing/create"
						permission={isAdminHQWithFinancePlus}
					>
						<CreateOfficeBilling />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/billing/:officeBillingId"
						permission={isAdminHQWithFinancePlus}
					>
						<DetailOfficeBilling />
					</PrivateRoute>

					<PrivateRoute
						exact
						path="/portal"
						permission={isAdminHQPlus}
					>
						<Portal />
					</PrivateRoute>

					<PrivateRoute
						exact
						path="/portal/:portalId"
						permission={isAdminHQPlus}
					>
						<DetailPortal />
					</PrivateRoute>

					<PrivateRoute
						exact
						path="/setting-portal-agent"
						permission={isAdminHQPlus}
					>
						<SettingPortal />
					</PrivateRoute>

					<PrivateRoute
						exact
						path="/setting-agent"
						permission={isFranchiseOrBusinessAdmin}
					>
						<SettingAgent />
					</PrivateRoute>

					<PrivateRoute
						exact
						path="/handover"
						permission={!isAdminHQOfficeFinancePlus}
					>
						<Handover />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/handover/create"
						permission={isAgent}
					>
						<HandoverCreate />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/handover/create/:handoverId/:index"
						permission={!isAdminHQOfficeFinancePlus}
					>
						<HandoverCreate />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/handover/:handoverId"
						permission={!isAdminHQOfficeFinancePlus}
					>
						<HandoverDetail />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/handover/:handoverId/:index"
						permission={!isAdminHQOfficeFinancePlus}
					>
						<HandoverDetail />
					</PrivateRoute>

					<PrivateRoute
						exact
						path="/agent-receivable"
						permission={isAdminHQWithOfficeFinancePlusAndAgent}
					>
						<AgentCalculation />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/agent-receivable/:agentBillingId"
						permission={isAdminHQWithOfficeFinancePlusAndAgent}
					>
						<DetailAgentCalculation />
					</PrivateRoute>

					<PrivateRoute
						exact
						path="/banner"
						permission={isAdminHQPlus}
					>
						<Banner />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/album"
						permission={isAdminHQPlus}
					>
						<Album />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/album/create"
						permission={isAdminHQPlus}
					>
						<CreateAlbum />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/album/:albumId/edit"
						permission={isAdminHQPlus}
					>
						<CreateAlbum isEdit={true} />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/album/:albumId"
						permission={isAdminHQPlus}
					>
						<DetailAlbum />
					</PrivateRoute>

					<PrivateRoute
						exact
						path="/preference"
						permission={isAdminHQPlus}
					>
						<Preference />
					</PrivateRoute>

					{/* Blog */}
					<PrivateRoute
						exact
						path="/blog-posts"
						permission={isAdminBlog}
					>
						<Posts />
					</PrivateRoute>

					<PrivateRoute
						exact
						path="/blog-posts/create"
						permission={isAdminBlog}
					>
						<CreatePost />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/blog-posts/create/:articleId"
						permission={isAdminBlog}
					>
						<CreatePost />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/blog-posts/create/:articleId/:index"
						permission={isAdminBlog}
					>
						<CreatePost />
					</PrivateRoute>

					<PrivateRoute
						exact
						path="/blog-posts/:articleId/edit"
						permission={isAdminBlog}
					>
						<CreatePost isEdit={true} />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/blog-posts/:articleId/edit/:index"
						permission={isAdminBlog}
					>
						<CreatePost isEdit={true} />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/blog-posts/:articleId"
						permission={isAdminBlog}
					>
						<DetailPost />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/blog-posts/:articleId/:index"
						permission={isAdminBlog}
					>
						<DetailPost />
					</PrivateRoute>

					<PrivateRoute
						exact
						path="/blog-categories"
						permission={isAdminBlog}
					>
						<Categories />
					</PrivateRoute>

					<PrivateRoute
						exact
						path="/blog-tags"
						permission={isAdminBlog}
					>
						<Tags />
					</PrivateRoute>

					<PrivateRoute
						exact
						path="/clients"
						permission={isAgent || roles.includes(EEmployeeRole.admin)}
					>
						<Client />
					</PrivateRoute>

					<PrivateRoute
						exact
						path="/clients/:clientId/"
						permission={isAgent || roles.includes(EEmployeeRole.admin)}
					>
						<ClientDetail />
					</PrivateRoute>

					{/* MyList */}
					<PrivateRoute
						exact
						path="/my-list"
					>
						<MyLists />
					</PrivateRoute>

					<PrivateRoute
						exact
						path="/my-list/create"
						permission={isAgentOrAO}
					>
						<CreateMyList />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/my-list/:myListId/edit"
						permission={isAdminHQOfficeNonFinancePlus || isAgentOrAO}
					>
						<CreateMyList isEdit={true} />
					</PrivateRoute>

					<PrivateRoute
						exact
						path="/my-list/:myListId"
					>
						<DetailMyList />
					</PrivateRoute>

					<PrivateRoute
						exact
						path="/template-messages"
					>
						<TemplateMessage />
					</PrivateRoute>

					<PrivateRoute
						exact
						path="/template-messages/edit/:templateId?"
					>
						<EditTemplateMessage />
					</PrivateRoute>
					<PrivateRoute
						exact
						path="/config-app-logo"
						permission={isAdminHQPlus}
					>
						<ConfigAppLogo />
					</PrivateRoute>

					<PrivateRoute
						exact
						path="/app-config"
						permission={isAdminHQPlus}
					>
						<AppConfig />
					</PrivateRoute>

					<PrivateRoute
						exact
						path="/portal-checker"
						permission={isAdminHQPlus}
					>
						<PortalChecker />
					</PrivateRoute>

					<PrivateRoute
						exact
						path="/forbidden"
					>
						<Forbidden />
					</PrivateRoute>
					<PrivateRoute path="*">
						<Result
							status="404"
							title="404"
							subTitle="Sorry, the page you visited does not exist."
						/>
					</PrivateRoute>
				</Switch>
			</Router>
		</Suspense>
	);
};

export default RootNavigator;
